import React, { useState } from 'react';
import { Send, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { useSubmitPartnerNote } from '@/hooks/usePartnerProjects';

interface PartnerNoteFormProps {
  projectId: string;
}

export function PartnerNoteForm({ projectId }: PartnerNoteFormProps) {
  const [noteContent, setNoteContent] = useState('');
  const [mentionAssigned, setMentionAssigned] = useState(true);
  const [success, setSuccess] = useState(false);

  const submitMutation = useSubmitPartnerNote();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!noteContent.trim()) return;

    try {
      await submitMutation.mutateAsync({
        projectId,
        noteContent: noteContent.trim(),
        mentionAssigned,
      });

      setNoteContent('');
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch {
      // Error handled by mutation
    }
  };

  const characterCount = noteContent.length;
  const maxCharacters = 5000;
  const isOverLimit = characterCount > maxCharacters;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
      <h3 className="font-semibold text-gray-900 mb-4">Send a Note</h3>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Textarea */}
        <div>
          <textarea
            value={noteContent}
            onChange={(e) => setNoteContent(e.target.value)}
            placeholder="Write a message to the team..."
            rows={4}
            className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none ${
              isOverLimit ? 'border-red-300' : 'border-gray-300'
            }`}
            disabled={submitMutation.isPending}
          />
          <div className="flex justify-between mt-1">
            <span className="text-xs text-gray-500">
              Your note will be posted to the project updates
            </span>
            <span
              className={`text-xs ${
                isOverLimit ? 'text-red-600' : 'text-gray-500'
              }`}
            >
              {characterCount}/{maxCharacters}
            </span>
          </div>
        </div>

        {/* Mention option */}
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="mentionAssigned"
            checked={mentionAssigned}
            onChange={(e) => setMentionAssigned(e.target.checked)}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            disabled={submitMutation.isPending}
          />
          <label htmlFor="mentionAssigned" className="text-sm text-gray-700">
            Notify assigned team members
          </label>
        </div>

        {/* Error message */}
        {submitMutation.isError && (
          <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 px-3 py-2 rounded-lg">
            <AlertCircle size={16} />
            <span>
              {submitMutation.error instanceof Error
                ? submitMutation.error.message
                : 'Failed to send note'}
            </span>
          </div>
        )}

        {/* Success message */}
        {success && (
          <div className="flex items-center gap-2 text-green-600 text-sm bg-green-50 px-3 py-2 rounded-lg">
            <CheckCircle size={16} />
            <span>Note sent successfully!</span>
          </div>
        )}

        {/* Submit button */}
        <button
          type="submit"
          disabled={
            !noteContent.trim() ||
            isOverLimit ||
            submitMutation.isPending
          }
          className="w-full btn btn-primary flex items-center justify-center gap-2"
        >
          {submitMutation.isPending ? (
            <>
              <Loader2 size={18} className="animate-spin" />
              Sending...
            </>
          ) : (
            <>
              <Send size={18} />
              Send Note
            </>
          )}
        </button>
      </form>
    </div>
  );
}
